import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AuthService } from '../auth.service';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { PriceListDTO } from 'src/app/models/types/Product/PriceListDTO.type';
import { Price } from 'src/app/models/types/Product/Price.type';
import { BatchStockDTO, GlobalAreaDTO, HEWBatchStockDTO, StockCounters, StocksDTO, StoreStock, StoreStocks } from 'src/app/models/types/Product/Stock.type';
import { HighJewelryStockInfo } from 'src/app/models/types/Product/Product.type';
import { StrapDTO } from 'src/app/models/types/Strap/Strap.type';

@Injectable({
  providedIn: 'root'
})
export class StockAndPriceService {

  private readonly apiUrl = `${environment.apiUrl}/StockAndPrice`;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  /**
   * API per il reperimento della priceslist di un prodotto
   * GET /api/StockAndPrice/PriceList/{sapCode}/{store}
   *
   * @param sapCode sapCode del prodotto
   * @returns Ritorna il singolo prezzo del prodotto
   */
  getPriceBySapCode(sapCode: string): Observable<Price | null> {
    const isNotUserStore = this.authService.user?.isNotUserStore;
    const store = isNotUserStore ? this.authService.getDefaultStore() : this.authService.store;
    const url = `${this.apiUrl}/PriceList/${sapCode}/${store?.idStore}`;
    return (this.http.get(url) as Observable<PriceListDTO>)
      //Filtra tra i prezzi quelli con "priceListCode" uguale a quello dello store e ritorna il primo prezzo con "myPriceList" uguale a TRUE
      .pipe(
        map(prices => prices.filter(price => {
          return price.priceListCode === store?.priceListCode;
        })),
        map(prices => {
          return prices.find(p => p.myPriceList) || null
        }),
        catchError(e => throwError(() => e))
      );
  }

  /**
     * API per il reperimento della priceslist di un prodotto
     * GET /api/StockAndPrice/PriceList/{sapCode}/{store}
     *
     * @param sapCode sapCode del prodotto
     * @returns Ritorna tutti i prezzi del prodotto
     */
  getPriceListBySapCode(sapCode: string): Observable<PriceListDTO> {
    const isNotUserStore = this.authService.user?.isNotUserStore;
    const store = isNotUserStore ? this.authService.getDefaultStore() : this.authService.store;
    const url = `${this.apiUrl}/PriceList/${sapCode}/${store?.idStore}`;
    return (this.http.get(url) as Observable<PriceListDTO>);
  }


  /**
   * API per il reperimento dello stock di un prodotto
   * GET /api/StockAndPrice/S4/Stock/Store/{store}/SapCode/{sapCode}
   * @param sapCode
   * @returns
   */
  getProductStock(sapCode: string, catCode: string): Observable<StocksDTO> {
    const url = `${this.apiUrl}/S4/Stock/Store/${this.authService.store?.idStore}/SapCode/${sapCode}`;
    let params = new HttpParams();
    if (catCode) {
      params = params.set('catCode', catCode);
    }
    return (this.http.get(url, { params }) as Observable<StocksDTO>);
  }

  /**
   * API per il reperimento dello stock di un prodotto per un utente HQ
   * GET /api/StockAndPrice/S4/Stock/HQ/company/{company}/SapCode/{sapCode}
   * @param sapCode
   * @param catCode
   * @param companyCode
   * @returns
   */
  getProductStock_HQ(sapCode: string, catCode: string, companyCode: string): Observable<StocksDTO> {
    const url = `${this.apiUrl}/S4/Stock/HQ/company/${companyCode}/SapCode/${sapCode}`;
    let params = new HttpParams();
    if (catCode) {
      params = params.set('catCode', catCode);
    }
    return (this.http.get(url, { params }) as Observable<StocksDTO>);
  }

  /**
   * API per il reperimento dello stock di un prodotto per un utente HQ
   * GET /api/StockAndPrice/S4/Stock/Batch/HQ/Company/{company}/CatCode/{catCode}
   * @param catCode
   * @param companyCode
   * @returns
   */
  getBatchProductStock_HQ(catCode: string, companyCode: string): Observable<BatchStockDTO> {
    const url = `${this.apiUrl}/S4/Stock/Batch/HQ/company/${companyCode}/CatCode/${catCode}`;
    let params = new HttpParams();
    if (catCode) {
      params = params.set('catCode', catCode);
    }
    return (this.http.get(url, { params }) as Observable<BatchStockDTO>);
  }

  /**
   * API per il reperimento delle informazione di stock peri i prodotti di alta gioielleria
   * GET /api/StockAndPrice/Stock/SapCode/{sapCode}
   * @param sapCode
   * @returns
   */
  getHighJewelryStockInfo(sapCode: string): Observable<HighJewelryStockInfo> {
    const url = `${this.apiUrl}/Stock/SapCode/${sapCode}`;
    return (this.http.get(url) as Observable<HighJewelryStockInfo>);
  }

  /**
   * API per il reperimento dello stock di un prodotto a Batch
   * GET /api/StockAndPrice/S4/Stock/Batch/Store/{store}/CatCode/{catCode}
   * @param catCode
   * @returns
   */
  getBatchProductStock(catCode: string): Observable<BatchStockDTO> {
    const url = `${this.apiUrl}/S4/Stock/Batch/Store/${this.authService.store?.idStore}/CatCode/${catCode}`;
    return (this.http.get(url) as Observable<BatchStockDTO>);
  }

  /**
   * API per il reperimento dello stock di un prodotto a Batch di alta orologeria
   * GET /api/StockAndPrice/Batch/SapCode/{sapCode}
   * @param sapCode
   * @returns
   */
  getHighEndWatchesStock(sapCode: string): Observable<HEWBatchStockDTO> {
    const url = `${this.apiUrl}/Batch/SapCode/${sapCode}`;
    return (this.http.get(url) as Observable<HEWBatchStockDTO>);
  }

  /**
   * Dato lo stock di uno store come parametro, ritorna i conatatori di free, reservation, consignementin e instransit
   * @param stock
   * @returns
   */
  getStockCounters(stock: StoreStock): StockCounters {
    return {
      free: stock.mainStock.free,
      reservation: stock.mainStock.reservation || 0,
      consignmentIn: stock.consignmentIn.free,
      inTransit: stock.inTransit?.reduce((acc, curr) => acc + (curr.quantity || 0), 0) || 0
    }
  }

  getStoreStockCounterTotal(stores: StoreStocks): StockCounters {
    let total: StockCounters = { free: 0, reservation: 0, consignmentIn: 0, inTransit: 0 };
    stores.forEach(store => {
      let counters = this.getStockCounters(store);
      total.free += counters.free;
      total.reservation += counters.reservation;
      total.consignmentIn += counters.consignmentIn;
      total.inTransit += counters.inTransit;
    });
    return total;

  }


  /**
   * API Ottieni lo stock dei cinturini.
   * GET /api/StockAndPrice/S4/Stock/Straps/Company/{company}/Store/{store}/SapCode/{sapCode}
   * @param sapCode
   */
  searchStraps(sapCode: string, companyCode?: string): Observable<StrapDTO> {
    const isNotUserStore = this.authService.user?.isNotUserStore;
    const store = isNotUserStore ? this.authService.getDefaultStore() : this.authService.store;
    const url = `${this.apiUrl}/S4/Stock/Straps/Company/${companyCode || store?.codeCompany}/Store/${store?.idStore}/SapCode/${sapCode}`;
    return this.http.get(url) as Observable<StrapDTO>;
  }

  /**
   * API per il reperimento delle aree di competenza degli utenti GlobalHQ
   * GET /api/StockAndPrice/StockHQ/GlobalAreas
   */
  getGlobalAreas(): Observable<GlobalAreaDTO> {
    const url = `${this.apiUrl}/StockHQ/GlobalAreas`;
    return this.http.get(url) as Observable<GlobalAreaDTO>;
  }

}
